import React, { useRef, useState } from 'react';
import { Upload, X, FileAudio, FileImage, File, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// Maximum file size in bytes (2MB for images, 5MB for audio to stay under LocalStorage limits)
const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2MB
const MAX_AUDIO_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_DOC_SIZE = 2 * 1024 * 1024; // 2MB

export const FileUploader = ({ 
  onFileSelect, 
  accept = "*/*", 
  label = "Téléverser un fichier",
  currentFile = null,
  currentValue = null, // URL or base64 value to show as current
  fileType = "file" // "audio", "image", "document", "file"
}) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState(currentFile || currentValue);
  const [fileName, setFileName] = useState(currentValue ? 'Fichier existant' : '');
  const [isLoading, setIsLoading] = useState(false);

  // Update preview when currentValue changes (for edit mode)
  React.useEffect(() => {
    if (currentValue) {
      setPreview(currentValue);
      setFileName('Fichier existant');
    }
  }, [currentValue]);

  const getMaxSize = () => {
    switch (fileType) {
      case 'audio': return MAX_AUDIO_SIZE;
      case 'image': return MAX_IMAGE_SIZE;
      case 'document': return MAX_DOC_SIZE;
      default: return MAX_IMAGE_SIZE;
    }
  };

  const getFileIcon = () => {
    switch (fileType) {
      case 'audio': return <FileAudio className="h-8 w-8 text-green-500" />;
      case 'image': return <FileImage className="h-8 w-8 text-blue-500" />;
      default: return <File className="h-8 w-8 text-gray-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    const maxSize = getMaxSize();
    
    // Check file size
    if (file.size > maxSize) {
      toast.error(`Le fichier est trop volumineux. Maximum: ${formatFileSize(maxSize)}`);
      return;
    }

    setIsLoading(true);
    setFileName(file.name);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Data = e.target.result;
        setPreview(base64Data);
        onFileSelect(base64Data, file.name, file.type);
        setIsLoading(false);
        toast.success(`Fichier "${file.name}" chargé avec succès`);
      };
      reader.onerror = () => {
        toast.error('Erreur lors de la lecture du fichier');
        setIsLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Erreur lors du chargement du fichier');
      setIsLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setPreview(null);
    setFileName('');
    onFileSelect(null, null, null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isUrl = (str) => {
    return str && (str.startsWith('http://') || str.startsWith('https://'));
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium flex items-center space-x-2">
        {getFileIcon()}
        <span>{label}</span>
      </label>

      {/* Warning about LocalStorage limits */}
      <div className="flex items-start space-x-2 p-3 bg-orange-500/10 rounded-lg text-sm">
        <AlertCircle className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" />
        <span className="text-orange-700 dark:text-orange-400">
          Taille max: {formatFileSize(getMaxSize())}. Pour des fichiers plus gros, utilisez un lien externe (Google Drive, Dropbox).
        </span>
      </div>

      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
          isDragging 
            ? 'border-primary bg-primary/5' 
            : preview 
            ? 'border-green-500 bg-green-500/5' 
            : 'border-border hover:border-primary/50 hover:bg-secondary/30'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleInputChange}
          className="hidden"
        />

        {isLoading ? (
          <div className="flex flex-col items-center space-y-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-sm text-muted-foreground">Chargement...</p>
          </div>
        ) : preview ? (
          <div className="space-y-3">
            {fileType === 'image' && !isUrl(preview) ? (
              <img 
                src={preview} 
                alt="Preview" 
                className="max-h-32 mx-auto rounded-lg object-contain"
              />
            ) : fileType === 'audio' && !isUrl(preview) ? (
              <audio 
                src={preview} 
                controls 
                className="w-full"
              />
            ) : (
              <div className="flex items-center justify-center">
                {getFileIcon()}
              </div>
            )}
            <p className="text-sm font-medium text-green-600 truncate max-w-full">
              {fileName || 'Fichier chargé'}
            </p>
            <button
              onClick={handleRemove}
              className="inline-flex items-center space-x-1 px-3 py-1 rounded-lg bg-red-500/10 text-red-600 hover:bg-red-500/20 transition-colors text-sm"
            >
              <X className="h-4 w-4" />
              <span>Supprimer</span>
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="h-10 w-10 mx-auto text-muted-foreground" />
            <p className="text-sm font-medium">
              Glissez-déposez un fichier ici ou cliquez pour parcourir
            </p>
            <p className="text-xs text-muted-foreground">
              {fileType === 'audio' && 'MP3, WAV, OGG acceptés'}
              {fileType === 'image' && 'JPG, PNG, WEBP acceptés'}
              {fileType === 'document' && 'PDF, DOC, DOCX acceptés'}
            </p>
          </div>
        )}
      </div>

      {/* URL alternative input */}
      <div className="relative">
        <div className="absolute inset-x-0 top-0 flex items-center justify-center">
          <span className="bg-background px-2 text-xs text-muted-foreground -translate-y-1/2">ou entrez une URL</span>
        </div>
        <input
          type="url"
          placeholder="https://example.com/fichier..."
          value={isUrl(preview) ? preview : ''}
          onChange={(e) => {
            const url = e.target.value;
            if (url) {
              setPreview(url);
              setFileName('URL externe');
              onFileSelect(url, 'URL externe', 'url');
            }
          }}
          className="w-full h-10 px-4 mt-3 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        />
      </div>
    </div>
  );
};
